import { Injectable, inject, isDevMode } from "@angular/core";
import {
  FirestoreDataConverter,
  QueryConstraint,
  WithFieldValue,
  collection,
  doc,
  query,
  setDoc,
  updateDoc,
  Firestore,
} from "firebase/firestore";
import { collectionData, docData, FIRESTORE } from "@dis/firebase";

import { Observable, tap } from "rxjs";

import { SnackbarService } from "./snackbar.service";

import { GoogleDriveApiService } from "@dis/gapi";
import { Build, FOLDERID, Job, Project } from "@dis/models";

@Injectable()
export abstract class FirestoreV9Service<T> {
  protected abstract basePath: string;
  protected abstract baseConverter: FirestoreDataConverter<T>;

  private serviceName = isDevMode() ? "Emulator" : "Firestore";

  protected firestore: Firestore = inject(FIRESTORE);
  protected driveApi: GoogleDriveApiService = inject(GoogleDriveApiService);
  protected snackbar: SnackbarService = inject(SnackbarService);

  doc$<D = T>(uid: string): Observable<D> {
    const docRef = doc(this.firestore, this.basePath, uid);
    // @ts-ignore
    return docData(docRef.withConverter(this.baseConverter)).pipe(
      tap((r) => {
        if (isDevMode()) {
          console.groupCollapsed(`${this.serviceName} Streaming [${this.basePath}] [doc$] ${uid}`);
          console.log(r);
          console.groupEnd();
        }
      })
    ) as Observable<D>;
  }

  private get collection() {
    return collection(this.firestore, this.basePath).withConverter(this.baseConverter);
  }

  /**
   * @param queryConstraints {QueryConstraint[]=[]} queryConstraints
   */
  collection$(queryConstraints: QueryConstraint[] = []): Observable<T[]> {
    return collectionData(query(this.collection, ...queryConstraints));
  }

  list$() {
    const colRef = collection(this.firestore, this.basePath);
    console.log(colRef.id);
  }

  public upsert(id: string | null | undefined, value: Partial<T> & { id?: string }) {
    return id ? this.update(id, value) : this.create(value);
  }

  private async create(value: Partial<T> & { id?: string }) {
    const docRef = doc(this.collection).withConverter(this.baseConverter);
    const id = docRef.id;
    value.id = id;
    await setDoc(docRef, value as WithFieldValue<T>);
  }

  private async update(id: string, value: Partial<T> & { id?: string }) {
    const docRef = doc(this.collection, id).withConverter(this.baseConverter);
    await updateDoc(docRef, value);
    // return of(id);
  }

  /** Helpers */

  private showStatus(message: string) {
    this.snackbar.openSnackBar(`${this.basePath.toUpperCase()} ${message}`);
  }

  public async checkFolder(resource: Project | Job | Build, parent?: string): Promise<string> {
    if (resource.folderId) {
      return resource.folderId;
    } else {
      const basePathKey = this.basePath as keyof typeof FOLDERID;
      const parentId = (FOLDERID[basePathKey] ? FOLDERID[basePathKey.toUpperCase() as keyof typeof FOLDERID] : parent) as string;
      const folder = await this.driveApi.createFolder(parentId, resource.title!);
      return folder.Id;
    }
  }

  public async deleteFile(id: string) {
    await this.driveApi.delete(id);
  }
}
